/**
 * Two-step sign-in (Roadmap 1845): pure helpers (have tests).
 *
 * After the Microsoft sign-in, the API may ask for a 6-digit code. When the
 * code is right it hands out a "proof" (valid 12 hours) that the web app sends
 * on every API call in the X-Proc-2fa header, and, with "Remember this
 * computer", a 30-day device token sent only to `api/two-factor/check`.
 */

export const PROOF_HEADER = "X-Proc-2fa";
export const DEVICE_HEADER = "X-Proc-2fa-Device";
export const TWO_STEP_REQUIRED_CODE = "two_factor_required";
/** Fired on window when the API answers 403 two_factor_required. */
export const TWO_STEP_REQUIRED_EVENT = "proc:two-step-required";

export const PROOF_STORAGE_KEY = "proc_2fa_proof";
export const DEVICE_STORAGE_KEY = "proc_2fa_devices";

export type TwoStepMethod = "app" | "email";
export type TwoStepState = "off" | "passed" | "code" | "setup";

export type StoredProof = { email: string; proof: string; expiresAt: string };
export type StoredDevices = Record<
  string,
  { token: string; expiresAt: string }
>;

const norm = (email: string | undefined | null) =>
  (email || "").trim().toLowerCase();

/** True for calls to our own API (the only place the proof is sent). */
export const isApiUrl = (url: string, apiBase: string): boolean => {
  if (!url || !apiBase) return false;
  const base = apiBase.replace(/\/+$/, "").toLowerCase();
  const target = url.toLowerCase();
  return target === base || target.startsWith(base + "/");
};

/** The API's 403 body for "enter your code first". */
export const isTwoStepRequiredBody = (body: unknown): boolean => {
  if (!body) return false;
  if (typeof body === "string") return body.includes(TWO_STEP_REQUIRED_CODE);
  if (typeof body === "object")
    return (body as { code?: unknown }).code === TWO_STEP_REQUIRED_CODE;
  return false;
};

/** Only digits, at most 6 ("123 456" -> "123456"). */
export const cleanCode = (value: string) =>
  value.replace(/\D/g, "").slice(0, 6);

/** Recovery codes: letters/digits, upper case, "ABCDE-FGHJK". */
export const cleanRecoveryCode = (value: string) => {
  const raw = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
  return raw.length > 5 ? `${raw.slice(0, 5)}-${raw.slice(5)}` : raw;
};

export const isCompleteCode = (value: string, recovery = false) =>
  recovery
    ? value.replace(/[^A-Z0-9]/gi, "").length === 10
    : /^\d{6}$/.test(value);

/** A valid, unexpired proof for this person (1 minute of margin). */
export const proofFor = (
  stored: StoredProof | null,
  email: string,
  now = Date.now(),
): string | null => {
  if (!stored || norm(stored.email) !== norm(email)) return null;
  const expires = Date.parse(stored.expiresAt);
  if (!Number.isFinite(expires) || expires - 60_000 <= now) return null;
  return stored.proof;
};

export const deviceTokenFor = (
  devices: StoredDevices | null,
  email: string,
  now = Date.now(),
): string | null => {
  const entry = devices?.[norm(email)];
  if (!entry) return null;
  const expires = Date.parse(entry.expiresAt);
  if (!Number.isFinite(expires) || expires <= now) return null;
  return entry.token;
};

export const withDevice = (
  devices: StoredDevices | null,
  email: string,
  token: string | null,
  expiresAt?: string | null,
): StoredDevices => {
  const next: StoredDevices = { ...(devices || {}) };
  if (token && expiresAt) next[norm(email)] = { token, expiresAt };
  else delete next[norm(email)];
  return next;
};

/** When to check again before the proof runs out (ms from now), or null. */
export const recheckDelay = (
  expiresAt: string | null | undefined,
  now = Date.now(),
): number | null => {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  if (!Number.isFinite(at)) return null;
  return Math.max(at - now - 2 * 60_000, 5_000);
};

/** "Chrome on Windows" for the remembered-computers label. */
export const deviceLabel = (userAgent: string) => {
  const ua = userAgent || "";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
    ? "Firefox"
    : /Chrome\//.test(ua)
    ? "Chrome"
    : /Safari\//.test(ua)
    ? "Safari"
    : "Browser";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /iPhone|iPad/.test(ua)
    ? "iPhone/iPad"
    : /Mac OS X/.test(ua)
    ? "Mac"
    : /Android/.test(ua)
    ? "Android"
    : /Linux/.test(ua)
    ? "Linux"
    : "a computer";
  return `${browser} on ${os}`;
};

/** The text file people download with their recovery codes. */
export const recoveryCodesText = (codes: string[], email: string, date: Date) =>
  [
    "ProCharted recovery codes",
    `Account: ${email}`,
    `Created: ${date.toDateString()}`,
    "",
    "Each code works once. Use one if you can't get a code from your app or email.",
    "",
    ...codes.map((c, i) => `${i + 1}. ${c}`),
    "",
  ].join("\n");

export const methodLabel = (method?: string | null) =>
  method === "app"
    ? "Authenticator app"
    : method === "email"
    ? "Email code"
    : "Not set up";

/** Plain-English message from an RTK Query / fetch error. */
export const twoStepErrorMessage = (error: unknown): string => {
  const e = error as { status?: number | string; data?: unknown } | undefined;
  const data = e?.data as { message?: string } | string | undefined;
  if (typeof data === "string" && data.trim() && data.length < 300) return data;
  if (data && typeof data === "object" && typeof data.message === "string")
    return data.message;
  if (e?.status === "FETCH_ERROR")
    return "Can't reach ProCharted right now. Check your internet connection and try again.";
  return "Something went wrong. Please try again.";
};
